import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Mail, KeyRound, Lock, ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import SEOHead from "../components/SEOHead";

/**
 * Customer + admin secure forgot-password (OTP-based, 3 sub-steps).
 *
 * Routes: /reset-password (no auth required)
 *
 * Step 1 → enter email → POST /api/auth/forgot-password
 * Step 2 → enter 6-digit OTP → POST /api/auth/verify-reset-otp → get short-lived reset_token
 * Step 3 → set new password → POST /api/auth/reset-password { reset_token, new_password }
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = location.state?.email || "";

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!email) { toast.error("Please enter your email"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If an account exists, we've emailed you a 6-digit code.");
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { toast.error("Enter the 6-digit code"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/verify-reset-otp", { email, otp });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const setNewPassword = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { reset_token: resetToken, new_password: pwd });
      toast.success("Password reset. Please sign in with your new password.");
      navigate("/login", { replace: true, state: { email } });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-x py-16 lg:py-24 max-w-md" data-testid="reset-password-page">
      <SEOHead title="Reset your password | Sri Durga Foods" description="Recover access to your Sri Durga Foods account." noindex />
      <div className="text-center mb-10">
        <span className="overline mb-3 block">Forgot Password</span>
        <h1 className="font-serif text-4xl text-brand-dark">
          {step === 1 && "Reset your password"}
          {step === 2 && "Enter the code"}
          {step === 3 && "Choose a new password"}
        </h1>
        <p className="text-sm text-brand-muted mt-3">
          {step === 1 && "We'll email you a 6-digit code to confirm it's you."}
          {step === 2 && `We sent a code to ${email}. It expires in 10 minutes.`}
          {step === 3 && "Make it strong — 10+ chars with a mix of letters, numbers and symbols."}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-brand-line p-8">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="reset-progress">
          {[1, 2, 3].map((n) => (
            <div key={n}
              className={`h-1.5 rounded-full transition-all ${n === step ? "w-8 bg-brand-chili" : n < step ? "w-4 bg-brand-chili/40" : "w-4 bg-brand-line"}`} />
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={requestOtp} className="space-y-5" data-testid="reset-step-email">
            <div>
              <label className="label-field">Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
                <input type="email" required className="input-field pl-9"
                  placeholder="you@example.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoFocus
                  data-testid="reset-email" />
              </div>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="reset-request-otp">
              {submitting ? "Sending code…" : (<><span>Send code</span> <ArrowRight className="w-4 h-4 inline ml-1" /></>)}
            </button>
            <div className="text-center text-sm">
              <button type="button" onClick={() => navigate("/login")}
                className="text-brand-chili hover:underline">Back to sign in</button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyOtp} className="space-y-5" data-testid="reset-step-otp">
            <div>
              <label className="label-field">6-digit code</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
                <input type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} required
                  className="input-field pl-9 font-mono text-center text-2xl tracking-widest"
                  placeholder="000000" value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  autoFocus data-testid="reset-otp" />
              </div>
              <p className="text-xs text-brand-muted mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Check spam if you don&apos;t see it. Up to 5 attempts per code.
              </p>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="reset-verify-otp">
              {submitting ? "Verifying…" : "Verify code"}
            </button>
            <div className="text-center text-sm">
              <button type="button" onClick={() => { setStep(1); setOtp(""); }}
                className="text-brand-chili hover:underline">Use a different email</button>
              <span className="mx-2 text-brand-muted">·</span>
              <button type="button" disabled={submitting} onClick={requestOtp}
                className="text-brand-chili hover:underline">Resend code</button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={setNewPassword} className="space-y-5" data-testid="reset-step-newpwd">
            <div>
              <label className="label-field">New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
                <input type="password" required className="input-field pl-9"
                  placeholder="At least 10 characters" value={pwd}
                  onChange={(e) => setPwd(e.target.value)} autoComplete="new-password" autoFocus
                  data-testid="reset-new-password" />
              </div>
            </div>
            <div>
              <label className="label-field">Confirm new password</label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
                <input type="password" required className="input-field pl-9"
                  placeholder="Re-enter password" value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)} autoComplete="new-password"
                  data-testid="reset-confirm-password" />
              </div>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="reset-submit">
              {submitting ? "Updating password…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
